import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { useQuery } from '@tanstack/react-query';
import { api } from '../hooks/useAuth';

const registerSchema = z.object({
  name: z.string().min(1, 'validation.required'),
  email: z.string().email('validation.email'),
  password: z.string().min(6, 'validation.minLength').refine((val) => val.length >= 6, 'validation.minLength'),
  locale: z.enum(['en', 'de', 'tr', 'ru']),
});

const Register = () => {
  const { t, i18n } = useTranslation();
  const { register: registerUser, isRegistering, registerError } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState('');

  // Fetch system settings for name and language
  const { data: settings } = useQuery({
    queryKey: ['public-settings'],
    queryFn: async () => {
      const response = await api.get('/settings/public');
      return response.data;
    },
    retry: false, // Don't retry on register page
  });

  const systemName = settings?.systemName || 'Invoice System';

  // Apply system language if available
  useEffect(() => {
    if (settings?.defaultLocale) {
      // Only apply if user hasn't manually changed language
      const savedLocale = localStorage.getItem('locale');
      if (!savedLocale) {
        i18n.changeLanguage(settings.defaultLocale);
      }
    }
  }, [settings, i18n]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      locale: i18n.language,
    },
  });

  const onSubmit = async (data) => {
    try {
      setError('');
      await registerUser(data);
      navigate('/dashboard');
    } catch (err) {
      setError(t('errors.networkError'));
    }
  };

  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
    { code: 'ru', name: 'Русский', flag: '🇷🇺' },
  ];

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h1 className="text-center text-2xl font-bold text-gray-900 mb-2">
            {systemName}
          </h1>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {t('auth.register')}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {t('auth.hasAccount')}{' '}
            <Link to="/login" className="font-medium text-primary-600 hover:text-primary-500">
              {t('auth.login')}
            </Link>
          </p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4">
            <div>
              <label htmlFor="name" className="label">
                {t('auth.name')}
              </label>
              <input
                {...register('name')}
                type="text"
                autoComplete="name"
                className={`input ${errors.name ? 'input-error' : ''}`}
                placeholder={t('auth.name')}
              />
              {errors.name && (
                <p className="error-message">{t(errors.name.message)}</p>
              )}
            </div>
            
            <div>
              <label htmlFor="email" className="label">
                {t('auth.email')}
              </label>
              <input
                {...register('email')}
                type="email"
                autoComplete="email"
                className={`input ${errors.email ? 'input-error' : ''}`}
                placeholder={t('auth.email')}
              />
              {errors.email && (
                <p className="error-message">{t(errors.email.message)}</p>
              )}
            </div>
            
            <div>
              <label htmlFor="password" className="label">
                {t('auth.password')}
              </label>
              <input
                {...register('password')}
                type="password"
                autoComplete="new-password"
                className={`input ${errors.password ? 'input-error' : ''}`}
                placeholder={t('auth.password')}
              />
              {errors.password && (
                <p className="error-message">{t(errors.password.message, { min: 6 })}</p>
              )}
            </div>
            
            <div>
              <label htmlFor="locale" className="label">
                {t('common.language')}
              </label>
              <select
                {...register('locale')}
                className={`input ${errors.locale ? 'input-error' : ''}`}
              >
                {languages.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.flag} {lang.name}
                  </option>
                ))}
              </select>
              {errors.locale && (
                <p className="error-message">{t(errors.locale.message)}</p>
              )}
            </div>
          </div>

          {(error || registerError) && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-600 text-sm">
                {error || registerError?.response?.data?.message || t('errors.networkError')}
              </p>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={isRegistering}
              className="btn btn-primary btn-lg w-full"
            >
              {isRegistering ? t('common.loading') : t('auth.registerButton')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Register;
